import { z } from 'zod';
import { addHourlyPremiumIssues } from './schemaHelpers';

const rateMultiplierSchema = z.object({
  code: z.string().optional(),
}).passthrough();

const shortTurnConfigSchema = z.object({
  surchargeType: z.string().nullable().optional(),
  requiredBreakHours: z.number().nullable().optional(),
  overtimeOffset: z.number().nullable().optional(),
  floorTierCode: z.string().nullable().optional(),
  floorTierCustomValue: z.number().nullable().optional(),
  shortTurnFixedSurchargeAmount: z.number().nullable().optional(),
  premiumType: z.string().nullable().optional(),
  premiumValue: z.number().nullable().optional(),
}).passthrough();

export const shortTurnSchema = z
  .object({
    usesShortTurn: z.boolean().nullable().optional(),
    shortTurnConfig: shortTurnConfigSchema.nullable().optional(),
    rateMultipliers: z.array(rateMultiplierSchema).nullable().optional(),
  })
  .passthrough()
  .superRefine((data, ctx) => {
    if (!data.usesShortTurn || !data.shortTurnConfig) return;

    const config = data.shortTurnConfig;
    const requiredBreak = config.requiredBreakHours;

    const additionalMultipliers = (data.rateMultipliers ?? []).some((m) => m.code !== 'STRAIGHT_TIME');

    if (!config.surchargeType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['shortTurnConfig.surchargeType'],
        message: 'Please select a Short-Turn Surcharge Method',
      });
    }

    if (
      config.surchargeType &&
      ['carry_over', 'bump_up', 'offset_overtime'].includes(config.surchargeType) &&
      !additionalMultipliers
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['shortTurnConfig.surchargeType'],
        message: 'Add at least one additional rate multiplier to use this surcharge method',
      });
    }

    if (requiredBreak == null || isNaN(requiredBreak) || requiredBreak <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['shortTurnConfig.requiredBreakHours'],
        message: 'Minimum Rest Period must be greater than 0',
      });
    }

    if (config.surchargeType === 'offset_overtime') {
      const offset = config.overtimeOffset;
      if (offset == null || !Number.isInteger(offset) || offset < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['shortTurnConfig.overtimeOffset'],
          message: 'Offset amount must be a whole number of at least 1.',
        });
      }
    }

    if (
      config.surchargeType === 'guaranteed_rate' &&
      !config.floorTierCode &&
      !config.floorTierCustomValue
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['shortTurnConfig.floorTierCode'],
        message: 'Select a floor multiplier or enter a custom rate',
      });
    }

    if (config.surchargeType === 'fixed_dollar') {
      if (config.shortTurnFixedSurchargeAmount === null || config.shortTurnFixedSurchargeAmount === undefined || config.shortTurnFixedSurchargeAmount <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['shortTurnConfig.shortTurnFixedSurchargeAmount'],
          message: 'Fixed surcharge amount must be greater than 0',
        });
      }
    } else if (config.surchargeType === 'hourly_premium') {
      addHourlyPremiumIssues(ctx, config.premiumType, config.premiumValue, 'shortTurnConfig.premiumType', 'shortTurnConfig.premiumValue');
      if (config.shortTurnFixedSurchargeAmount !== null && config.shortTurnFixedSurchargeAmount !== undefined && config.shortTurnFixedSurchargeAmount < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['shortTurnConfig.shortTurnFixedSurchargeAmount'],
          message: 'Fixed surcharge cannot be negative',
        });
      }
    }
  });

export type ShortTurnSchemaData = z.infer<typeof shortTurnSchema>;
